import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Rocket, Timer, Coins, Loader2, X, CheckCircle2 } from "lucide-react";
import { magicBoost, magicBoostStatus } from "@/lib/magicApi";

/**
 * BoostModal — spend coins to pin your profile to the top of the community
 * feed for 24 hours. If the member already has an active boost, this modal
 * offers to extend it.
 */
export default function BoostModal({ onClose, onBoosted, cost = 500, hours = 24 }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    magicBoostStatus().then(setStatus).catch(() => {
      toast.error("Couldn't check boost status"); onClose?.();
    });
  }, [onClose]);

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await magicBoost();
      const msg = r.extended ? `+${hours}h — boost extended!` : `Boosted for ${hours}h — you're at the top!`;
      toast.success(msg);
      onBoosted?.(r);
    } catch (e) {
      const detail = e.response?.data?.detail || "Couldn't boost";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const mine = status?.mine;
  const hoursLeft = mine ? Math.max(0, Math.round((new Date(mine.expires_at) - Date.now()) / 3600000)) : 0;

  return (
    <div data-testid="boost-modal" onClick={onClose}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl border border-[#FFD700]/50 bg-gradient-to-br from-[#150525] via-[#0A0512] to-[#150525] p-6 relative overflow-hidden">
        <button data-testid="boost-close" onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl bg-[#FFD700]/30" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-black/40 border border-[#FFD700]/40 text-[#FFD700] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
            <Rocket className="w-3 h-3" /> Boosted profile
          </div>

          {!status ? (
            <div className="py-8 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : mine ? (
            <>
              <h2 className="font-black text-2xl leading-tight">You're already boosted <span className="text-[#FFD700]">🚀</span></h2>
              <div className="mt-3 rounded-2xl bg-black/40 border border-[#FFD700]/30 p-4 flex items-center gap-3" data-testid="boost-active">
                <div className="w-12 h-12 rounded-full bg-[#FFD700] text-[#0A0512] flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-lg">{hoursLeft}h left</div>
                  <div className="text-[11px] text-white/60">Expires {new Date(mine.expires_at).toLocaleString()}</div>
                </div>
              </div>
              <div className="text-sm text-white/70 mt-4">
                Want to stay pinned longer? Extend for another {hours}h at {cost} coins.
              </div>
              <button data-testid="boost-extend-btn" onClick={activate} disabled={busy}
                className="mt-5 w-full inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#FFD700] to-[#FF7A00] text-black font-black text-sm uppercase tracking-widest px-5 py-3 rounded-full disabled:opacity-40 shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Extending…</>
                     : <><Coins className="w-4 h-4" fill="currentColor" /> Extend +{hours}h · {cost} coins</>}
              </button>
            </>
          ) : (
            <>
              <h2 className="font-black text-2xl leading-tight">Jump the queue.</h2>
              <p className="text-sm text-white/70 mt-2">
                Boost your profile and your latest creation gets pinned to the very top of the community feed — with a glowing ribbon — for the next {hours} hours.
              </p>
              <div className="mt-5 rounded-2xl border border-[#FFD700]/30 bg-black/40 p-4 space-y-2">
                <Row icon={<Rocket className="w-4 h-4 text-[#FFD700]" />} label="Pinned at the top" value="For all viewers" />
                <Row icon={<Timer className="w-4 h-4 text-[#FFD700]" />} label="Duration" value={`${hours} hours`} />
                <Row icon={<Coins className="w-4 h-4 text-[#FFD700]" />} label="Cost" value={`${cost} coins`} />
              </div>
              <button data-testid="boost-confirm-btn" onClick={activate} disabled={busy}
                className="mt-5 w-full inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#FFD700] to-[#FF7A00] text-black font-black text-sm uppercase tracking-widest px-5 py-3 rounded-full disabled:opacity-40 shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Boosting…</>
                     : <><Rocket className="w-4 h-4" /> Boost for {cost} coins</>}
              </button>
              <div className="mt-3 text-center text-[10px] text-white/40 uppercase tracking-widest">
                {status.active_total} creator{status.active_total === 1 ? "" : "s"} currently boosted
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <span className="text-white/70">{label}</span>
      <span className="ml-auto font-black text-white">{value}</span>
    </div>
  );
}
